import React, { useEffect, useMemo, useState } from 'react'
import ReactModal from 'react-modal'
import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID } from '../../../../graphql/query'
import InteractiveFormPreview from './InteractiveFormPreview'
import DeleteFormModal from './DeleteFormModal'
import ExpiryDateModal from './ExpiryDateModal'
import {
  getFormPreviewStats,
  getRenderablePageElements,
  resolveFormPages
} from './formPreviewUtils'
import { getFormTypeBadgeClass, getFormTypeLabel } from './formTypeUtils'
import './FormCardPreviewModal.scss'

export interface FormPreviewInfo {
  formUuid: string
  name: string
  description?: string
  formData?: any
  cardStyle?: string
  cardBackground?: string
  cardstyle?: string
  isPrefillForm?: boolean
  formStatus?: string
  expiryDate?: string | null
  createdAt?: string
  updatedAt?: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  formInfo: FormPreviewInfo | null
}

const modalStyles: ReactModal.Styles = {
  overlay: { zIndex: 1050, backgroundColor: 'rgba(0, 0, 0, 0.55)' },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(1120px, 96vw)',
    height: 'min(88vh, 860px)',
    padding: 0,
    border: 'none',
    borderRadius: '12px',
    overflow: 'hidden'
  }
}

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const formatExpiry = (value?: string | null) => {
  if (!value) return 'No expiry'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return 'No expiry'
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const nestedModalStyles: ReactModal.Styles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-30%, -60%)'
  }
}

const expiryModalStyles: ReactModal.Styles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '15px',
    border: 'none',
    boxShadow: '0 2px 8px 0 #00000029'
  }
}

const FormCardPreviewModal: React.FC<Props> = ({ isOpen, onClose, formInfo }) => {
  const [pageIndex, setPageIndex] = useState(0)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isExpiryModalOpen, setIsExpiryModalOpen] = useState(false)
  const [, setIsLoading] = useState(false)

  const { data: fetchedData, loading } = useQuery(GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID, {
    variables: { input: { formUuid: formInfo?.formUuid } },
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } },
    skip: !isOpen || !formInfo?.formUuid,
    fetchPolicy: 'network-only'
  })

  const resolvedRecord = fetchedData?.getAllFormBuilderByBgAccountId?.data?.[0] ?? formInfo
  const formDataSource = resolvedRecord?.formData ?? formInfo?.formData

  const pages = useMemo(() => resolveFormPages(formDataSource), [formDataSource])
  const stats = useMemo(() => getFormPreviewStats(formDataSource), [formDataSource])
  const hasPreviewFields = useMemo(
    () => pages.some((page) => getRenderablePageElements(page).length > 0),
    [pages]
  )

  const cardStyle =
    resolvedRecord?.cardStyle ||
    resolvedRecord?.cardstyle ||
    formInfo?.cardStyle ||
    formInfo?.cardstyle ||
    ''
  const cardBackground = resolvedRecord?.cardBackground || formInfo?.cardBackground || ''
  const displayName =
    (resolvedRecord?.name || formInfo?.name)?.replace(/[_\-]/g, ' ').replace(/[^\w\s]/gi, '') ||
    'Form'
  const isPrefillForm = resolvedRecord?.isPrefillForm ?? formInfo?.isPrefillForm
  const expiryDate = resolvedRecord?.expiryDate ?? formInfo?.expiryDate
  const recipientsPath = `/automation/process-automation/form-builder/share-form/${formInfo?.formUuid}/prefill-recipients`

  useEffect(() => {
    if (isOpen) setPageIndex(0)
  }, [isOpen, formInfo?.formUuid])

  useEffect(() => {
    if (!isOpen) {
      setIsDeleteModalOpen(false)
      setIsExpiryModalOpen(false)
    }
  }, [isOpen])

  const handleClose = () => {
    setPageIndex(0)
    onClose()
  }

  if (!formInfo) return null

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      ariaHideApp={false}
      style={modalStyles}
      contentLabel='Form preview'
    >
      <div className='form-card-preview'>
        <header className='form-card-preview__header'>
          <div className='form-card-preview__header-main'>
            <div className='form-card-preview__header-icon'>
              <i className='bi bi-eye' />
            </div>
            <div className='min-w-0'>
              <h5 className='form-card-preview__title'>Form preview</h5>
              <small className='form-card-preview__subtitle'>{displayName}</small>
            </div>
          </div>
          <button type='button' className='btn-close' aria-label='Close' onClick={handleClose} />
        </header>

        <div className='form-card-preview__body'>
          <aside className='form-card-preview__info'>
            <h6 className='form-card-preview__info-name'>{displayName}</h6>
            <div className='form-card-preview__badges'>
              <span className={`badge ${getFormTypeBadgeClass(resolvedRecord ?? formInfo)}`}>
                {getFormTypeLabel(resolvedRecord ?? formInfo)}
              </span>
              {formInfo.formStatus && (
                <span className='badge bg-light text-dark border'>{formInfo.formStatus}</span>
              )}
            </div>

            <p className='form-card-preview__description'>
              {(resolvedRecord?.description || formInfo.description)?.trim() ||
                'No description provided.'}
            </p>

            <div className='form-card-preview__stats'>
              <div className='form-card-preview__stat'>
                <span className='form-card-preview__stat-label'>Fields</span>
                <span className='form-card-preview__stat-value'>{stats.inputFieldCount}</span>
              </div>
              <div className='form-card-preview__stat'>
                <span className='form-card-preview__stat-label'>Pages</span>
                <span className='form-card-preview__stat-value'>{stats.pageCount}</span>
              </div>
              <div className='form-card-preview__stat'>
                <span className='form-card-preview__stat-label'>Expiry</span>
                <span className='form-card-preview__stat-value'>{formatExpiry(expiryDate)}</span>
              </div>
              <div className='form-card-preview__stat'>
                <span className='form-card-preview__stat-label'>Created</span>
                <span className='form-card-preview__stat-value'>
                  {formatDate(resolvedRecord?.createdAt || formInfo.createdAt)}
                </span>
              </div>
            </div>

            {stats.pageCount > 1 && (
              <div className='form-card-preview__pages-section'>
                <p className='form-card-preview__actions-label'>Pages</p>
                <div className='form-card-preview__pages'>
                  {pages.map((_, idx) => (
                    <button
                      key={idx}
                      type='button'
                      className={`form-card-preview__page-tab ${
                        pageIndex === idx ? 'form-card-preview__page-tab--active' : ''
                      }`}
                      onClick={() => setPageIndex(idx)}
                    >
                      <i className='bi bi-file-earmark-text' />
                      Page {idx + 1}
                    </button>
                  ))}
                </div>
                <small className='text-muted d-block mt-2'>
                  Jump to a page in the interactive preview below.
                </small>
              </div>
            )}

            <div className='form-card-preview__actions'>
              <p className='form-card-preview__actions-label'>Actions</p>
              <Link
                to={`/automation/edit-form/${formInfo.formUuid}`}
                className='form-card-preview__action-btn form-card-preview__action-btn--primary'
                onClick={handleClose}
              >
                <i className='bi bi-pencil' />
                Edit form
              </Link>
              <Link
                to={`/automation/process-automation/form-builder/share-form/${formInfo.formUuid}`}
                className='form-card-preview__action-btn'
                onClick={handleClose}
              >
                <i className='bi bi-share' />
                Share
              </Link>
              <Link
                to={`/automation/table/${formInfo.formUuid}`}
                className='form-card-preview__action-btn'
                onClick={handleClose}
              >
                <i className='bi bi-table' />
                View submissions
              </Link>
              {isPrefillForm && (
                <Link
                  to={recipientsPath}
                  className='form-card-preview__action-btn'
                  onClick={handleClose}
                >
                  <i className='bi bi-people' />
                  View recipients
                </Link>
              )}
              <button
                type='button'
                className='form-card-preview__action-btn'
                onClick={() => setIsExpiryModalOpen(true)}
              >
                <i className='bi bi-calendar' />
                Expiry date
              </button>
              <button
                type='button'
                className='form-card-preview__action-btn form-card-preview__action-btn--danger'
                onClick={() => setIsDeleteModalOpen(true)}
              >
                <i className='bi bi-trash' />
                Delete form
              </button>
            </div>
          </aside>

          <section className='form-card-preview__preview'>
            <div className='form-card-preview__preview-toolbar'>
              <span className='form-card-preview__preview-label'>
                <i className='bi bi-phone' />
                Live preview
              </span>
              {stats.pageCount > 1 && (
                <span className='form-card-preview__page-indicator'>
                  Page {pageIndex + 1} of {stats.pageCount}
                </span>
              )}
            </div>
            <div className='form-card-preview__preview-scroll'>
              {loading && !formDataSource ? (
                <div className='form-card-preview__loading'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Loading preview...</span>
                  </div>
                  <p className='mt-3 mb-0 small'>Loading form preview...</p>
                </div>
              ) : !hasPreviewFields ? (
                <div className='form-card-preview__empty'>
                  <div className='form-card-preview__empty-icon'>
                    <i className='bi bi-file-earmark-x' />
                  </div>
                  <p className='mb-1 fw-semibold'>No form fields to preview</p>
                  <small className='text-muted'>
                    Open the form in the editor and add fields, or fix imported form data.
                  </small>
                </div>
              ) : (
                <>
                  <InteractiveFormPreview
                    formData={pages}
                    cardStyle={cardStyle}
                    cardBackground={cardBackground}
                    isPrefillForm={!!isPrefillForm}
                    initialPageIndex={pageIndex}
                    onPageChange={setPageIndex}
                  />
                  <p className='interactive-form-preview__preview-note mb-0 px-3 pb-3'>
                    Preview mode — submissions are not saved.
                  </p>
                </>
              )}
            </div>
          </section>
        </div>
      </div>

      <ReactModal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        style={nestedModalStyles}
        contentLabel='Delete form'
        ariaHideApp={false}
      >
        <DeleteFormModal
          formInfo={resolvedRecord}
          setIsLoading={setIsLoading}
          closeDeleteModal={() => {
            setIsDeleteModalOpen(false)
            handleClose()
          }}
        />
      </ReactModal>

      <ReactModal
        isOpen={isExpiryModalOpen}
        onRequestClose={() => setIsExpiryModalOpen(false)}
        style={expiryModalStyles}
        contentLabel='Update expiry'
        ariaHideApp={false}
      >
        <ExpiryDateModal
          formInfo={resolvedRecord}
          closeExpiryModal={() => setIsExpiryModalOpen(false)}
        />
      </ReactModal>
    </ReactModal>
  )
}

export default FormCardPreviewModal
